import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, AlertCircle } from 'lucide-react';
import { API_BASE } from '../config';
import { formatDate } from '../utils/formatDate';
import { exportSheet, todayStamp } from '../excel';
import { hasEditAccess } from '../permissionConfig';
import { EMBEDDED_CSS, FONT, FluidStatusTabs, KpiBento, DmsHeader } from '../dmsTheme';
import DistributorSkuMasterTable from '../components/DistributorSkuMasterTable';

/* ============================================================================
 * Fatehpuria Inventory — /dms/fatehpuria-inventory
 *
 * Restyled onto the GRN token set (see dmsTheme.js). Dashboard tab (default)
 * now shows the sales-based, per-zone 20-day projection (same methodology as
 * Uprising's projection) instead of the old open-PO-cover-days view. Open PO
 * upload and Location Mapping are removed entirely per the user's explicit
 * instruction; SKU Mapping is the shared DistributorSkuMasterTable.
 * ========================================================================== */

const STATUS = {
  OUT: { label: 'Out of Stock', bg: 'var(--grn-pink-tint)', fg: 'var(--grn-error)', border: '#EFD2DA' },
  CRITICAL: { label: 'Critical (<10d)', bg: 'var(--grn-pink-tint)', fg: 'var(--grn-error)', border: '#EFD2DA' },
  LOW: { label: 'Low (10-20d)', bg: 'var(--grn-amber-tint)', fg: 'var(--grn-amber)', border: 'var(--grn-amber-border)' },
  HEALTHY: { label: 'Healthy (20d+)', bg: 'var(--grn-green-tint)', fg: 'var(--grn-green)', border: 'var(--grn-green-border)' },
  NON_MOVING: { label: 'Non-Moving', bg: 'var(--grn-surface-subtle)', fg: 'var(--grn-text-muted)', border: 'var(--grn-border)' },
};
const STATUS_ORDER = ['OUT', 'CRITICAL', 'LOW', 'HEALTHY', 'NON_MOVING'];
const ZONES = ['North', 'South', 'East', 'West'];
const ZONE_CITY = { North: 'Gurgaon', South: 'Bangalore', East: 'Kolkata', West: 'Mumbai' };

function fmtInt(v) { return v === null || v === undefined ? '—' : Number(v).toLocaleString('en-IN'); }

const card = { background: 'var(--grn-surface)', borderRadius: 16, boxShadow: 'var(--grn-shadow-sm)', border: '1px solid var(--grn-border)' };
const TABS = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'projection', label: '20-Day Projection' },
  { key: 'stock', label: 'Stock Status' },
  { key: 'sales', label: 'Sales' },
  { key: 'mapping', label: 'SKU Master' },
];

export default function FatehpuriaInventory({ user }) {
  const canEdit = hasEditAccess(user, 'dms', 'fatehpuria_inventory');
  const [tab, setTab] = useState('dashboard');
  const [zoneFilter, setZoneFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [proj, setProj] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadMsgs, setUploadMsgs] = useState({});
  const [uploadingKey, setUploadingKey] = useState(null);
  const [salesRows, setSalesRows] = useState([]);
  const [salesSearch, setSalesSearch] = useState('');
  const [salesZone, setSalesZone] = useState('');
  const [dash, setDash] = useState(null);
  const [dashLoading, setDashLoading] = useState(true);
  const [stockRows, setStockRows] = useState([]);
  const [stockSearch, setStockSearch] = useState('');
  const [stockZone, setStockZone] = useState('');

  const loadStock = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/dms?action=stock&distributor=Fatehpuria`);
      const body = await res.json();
      setStockRows(Array.isArray(body) ? body : []);
    } catch (e) { /* non-fatal */ }
  }, []);
  useEffect(() => { loadStock(); }, [loadStock]);

  const loadDashboard = useCallback(async () => {
    setDashLoading(true);
    try {
      const res = await fetch(`${API_BASE}/fatehpuria-inventory?action=dashboard`);
      const body = await res.json();
      if (res.ok) setDash(body);
    } catch (e) { /* non-fatal */ }
    finally { setDashLoading(false); }
  }, []);
  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const loadSales = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/dms?action=sales&distributor=Fatehpuria`);
      const body = await res.json();
      setSalesRows(Array.isArray(body) ? body : []);
    } catch (e) { /* non-fatal */ }
  }, []);
  useEffect(() => { loadSales(); }, [loadSales]);

  const loadProjection = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API_BASE}/fatehpuria-inventory?action=projection`);
      const body = await res.json();
      if (!res.ok || body?.success === false) throw new Error(body?.error || 'Failed to load');
      setProj(body);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { loadProjection(); }, [loadProjection]);

  async function handleSalesUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingKey('sales');
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${API_BASE}/fatehpuria-inventory?action=upload_sales`, { method: 'POST', body: form });
      const body = await res.json();
      if (res.ok && body.success) {
        setUploadMsgs((m) => ({ ...m, sales: { type: 'success', text: `${body.inserted} rows uploaded for ${body.month_year}${body.needs_mapping?.length ? `, ${body.needs_mapping.length} unmapped (fix in SKU Master)` : ''}` } }));
        loadSales(); loadProjection();
      } else {
        setUploadMsgs((m) => ({ ...m, sales: { type: 'error', text: body?.error || 'Upload failed' } }));
      }
    } catch (err) {
      setUploadMsgs((m) => ({ ...m, sales: { type: 'error', text: 'Network error' } }));
    } finally { setUploadingKey(null); e.target.value = ''; }
  }

  async function handleClosingStockUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingKey('closing');
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${API_BASE}/fatehpuria-inventory?action=upload_closing_stock`, { method: 'POST', body: form });
      const body = await res.json();
      if (res.ok && body.success) {
        setUploadMsgs((m) => ({ ...m, closing: { type: 'success', text: `${body.inserted} SKU/zone rows uploaded${body.needs_mapping?.length ? `, ${body.needs_mapping.length} unmapped (fix in SKU Master)` : ''}` } }));
        loadProjection();
        loadStock();
      } else {
        setUploadMsgs((m) => ({ ...m, closing: { type: 'error', text: body?.error || 'Upload failed' } }));
      }
    } catch (err) {
      setUploadMsgs((m) => ({ ...m, closing: { type: 'error', text: 'Network error' } }));
    } finally { setUploadingKey(null); e.target.value = ''; }
  }

  const allRows = proj?.rows || [];
  const filteredRows = allRows
    .filter((r) => !zoneFilter || r.zone === zoneFilter)
    .filter((r) => !statusFilter || r.status === statusFilter)
    .filter((r) => !search || r.item_name?.toLowerCase().includes(search.toLowerCase()));

  const totalStockQty = stockRows.reduce((s, r) => s + (Number(r.quantity) || 0), 0);
  const totalStockValue = stockRows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const filteredStock = stockRows
    .filter((r) => !stockZone || r.zone === stockZone)
    .filter((r) => !stockSearch || r.item_name?.toLowerCase().includes(stockSearch.toLowerCase()) || r.ean?.toLowerCase().includes(stockSearch.toLowerCase()));

  const handleExport = () => {
    if (!proj) return;
    exportSheet({
      headers: ['Zone', 'Item', 'EAN', 'Current Stock', 'Avg Daily Sales', 'Needed 20 Days', 'Gap', 'Days of Stock', 'Status'],
      rows: filteredRows.map((r) => [r.zone, r.item_name, r.ean || '', r.current_stock, r.avg_daily_sales, r.needed_20_days, r.gap, r.days_of_stock ?? '', r.status]),
      sheetName: 'Fatehpuria Projection',
      filename: `fatehpuria-projection_${todayStamp()}.xlsx`,
    });
  };

  return (
    <div data-tutorial="dms-fatehpuria-table" className="dms-root" style={{ maxWidth: 1400, margin: '0 auto', background: 'var(--grn-page-bg)' }}>
      <style>{EMBEDDED_CSS}</style>
      <DmsHeader
        title="Fatehpuria" accent="Distribution"
        subtitle={`${dash?.latest_stock_date ? `Stock as on ${formatDate(dash.latest_stock_date)} · ` : ''}Data shown is for the current month`}
        right={<>
          {uploadMsgs.closing && (
            <div style={{ padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, background: uploadMsgs.closing.type === 'success' ? 'rgba(92,126,96,0.25)' : 'rgba(194,94,119,0.25)', color: uploadMsgs.closing.type === 'success' ? '#B8E0BC' : '#F3B8C4' }}>
              {uploadMsgs.closing.text}
            </div>
          )}
          {uploadMsgs.sales && (
            <div style={{ padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, background: uploadMsgs.sales.type === 'success' ? 'rgba(92,126,96,0.25)' : 'rgba(194,94,119,0.25)', color: uploadMsgs.sales.type === 'success' ? '#B8E0BC' : '#F3B8C4' }}>
              {uploadMsgs.sales.text}
            </div>
          )}
          <label style={{ padding: '9px 18px', background: 'var(--grn-purple)', color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            {uploadingKey === 'closing' ? 'Uploading...' : 'Upload Stock File'}
            <input type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleClosingStockUpload} disabled={!!uploadingKey} />
          </label>
          <label style={{ padding: '9px 18px', background: '#fff', color: '#0B0A12', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            {uploadingKey === 'sales' ? 'Uploading...' : 'Upload Sales File'}
            <input type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleSalesUpload} disabled={!!uploadingKey} />
          </label>
          <button onClick={() => { loadDashboard(); loadProjection(); loadStock(); loadSales(); }}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 14px', background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            <RefreshCw size={14} /> Refresh
          </button>
        </>}
      />
      <div style={{ padding: '18px 22px 32px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <FluidStatusTabs tabs={TABS} value={tab} onChange={setTab} />

        {tab === 'dashboard' && (
          <>
            {dashLoading && !dash && <div style={{ ...card, padding: 46, textAlign: 'center', color: 'var(--grn-text-muted)' }}>Loading…</div>}
            {dash && (
              <>
                <KpiBento items={[
                  { label: 'Net Sales (Qty)', value: Number(dash.sales_kpi?.net_qty_sold || 0) },
                  { label: 'Sales Revenue', value: Number(dash.sales_kpi?.net_amount || 0), prefix: '₹' },
                  { label: 'SOH Value', value: Number(dash.stock_kpi?.total_stock_value || 0), prefix: '₹', sub: '(MRP not available)' },
                  { label: 'SOH Qty', value: Number(dash.stock_kpi?.total_stock_qty || 0) },
                  { label: 'Zero Stock SKUs', value: Number(dash.stock_kpi?.zero_stock_skus || 0) },
                ]} columns="repeat(5,1fr)" />

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div style={{ ...card, padding: '18px 20px' }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--grn-text)', marginBottom: 14 }}>Daily Net Sales <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--grn-text-muted)' }}>(<strong>Days</strong> on X axis, <strong>Pcs</strong> on Y axis)</span></div>
                    {dash.daily_trend?.length > 0 ? (
                      (() => {
                        const maxQ = Math.max(...dash.daily_trend.map((d) => d.qty), 1);
                        const minQ = Math.min(...dash.daily_trend.map((d) => d.qty), 0);
                        const mid = Math.round((maxQ + minQ) / 2);
                        const maxAbs = Math.max(...dash.daily_trend.map((d) => Math.abs(d.qty)), 1);
                        return (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minWidth: 42, height: 130, paddingBottom: 18 }}>
                              <div style={{ fontSize: 9, color: '#000', fontWeight: 700, textAlign: 'right' }}>{maxQ}</div>
                              <div style={{ fontSize: 9, color: '#000', fontWeight: 700, textAlign: 'right' }}>{mid}</div>
                              <div style={{ fontSize: 9, color: '#000', fontWeight: 700, textAlign: 'right' }}>{minQ}</div>
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 110, overflowX: 'auto' }}>
                                {dash.daily_trend.map((d) => {
                                  const pct = (Math.abs(d.qty) / maxAbs) * 100;
                                  return (
                                    <div key={d.date} title={`${d.date}: ${d.qty} pcs`} style={{ flex: '0 0 18px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%' }}>
                                      <div style={{ width: '100%', height: `${Math.max(pct, 4)}%`, background: d.qty < 0 ? 'var(--grn-error)' : 'var(--grn-purple)', borderRadius: '3px 3px 0 0' }} />
                                    </div>
                                  );
                                })}
                              </div>
                              <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
                                {dash.daily_trend.map((d) => (
                                  <div key={d.date} style={{ flex: '0 0 18px', textAlign: 'center' }}>
                                    <div style={{ fontSize: 9, color: '#000', fontWeight: 700 }}>{parseInt(d.date.slice(8, 10), 10)}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        );
                      })()
                    ) : (
                      <div style={{ textAlign: 'center', color: 'var(--grn-text-muted)', padding: 30 }}>No sales data yet</div>
                    )}
                  </div>

                  <div style={{ ...card, padding: '18px 20px' }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--grn-text)', marginBottom: 14 }}>Stock by Zone</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {(dash.zone_breakdown || []).map((z) => {
                        const maxQ = Math.max(...(dash.zone_breakdown || []).map((x) => Number(x.stock_qty) || 0), 1);
                        const pct = ((Number(z.stock_qty) || 0) / maxQ) * 100;
                        return (
                          <div key={z.zone}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                              <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--grn-text)' }}>{z.zone} ({ZONE_CITY[z.zone]})</span>
                              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--grn-text)' }}>{fmtInt(z.stock_qty)} pcs</span>
                            </div>
                            <div style={{ background: 'var(--grn-surface-subtle)', borderRadius: 4, height: 5 }}>
                              <div style={{ width: `${pct}%`, height: '100%', background: 'var(--grn-purple)', borderRadius: 4 }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div style={{ ...card, padding: '18px 20px' }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--grn-text)', marginBottom: 14 }}>Top Accounts <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--grn-text-muted)' }}>(Sales)</span></div>
                  {!dash.top_accounts?.length ? (
                    <div style={{ textAlign: 'center', color: 'var(--grn-text-muted)', padding: 20 }}>No account data yet</div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                      {dash.top_accounts.map((a, i) => (
                        <div key={i} style={{ background: 'var(--grn-surface-subtle)', borderRadius: 12, padding: '14px 16px', border: '1px solid var(--grn-border)' }}>
                          <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--grn-text)', marginBottom: 6 }}>{a.account}</div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--grn-purple)' }}>₹{Number(a.net_amount || 0).toLocaleString('en-IN')}</div>
                          <div style={{ fontSize: 11, color: 'var(--grn-text-muted)', marginTop: 2 }}>{a.order_count} orders</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div style={{ ...card, padding: '18px 20px' }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--grn-text)', marginBottom: 14 }}>Top SKUs by Sales</div>
                  <div style={{ overflowX: 'auto' }}>
                    <div style={{ minWidth: 900 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '0.5fr 2.2fr 1fr 1fr 1fr', background: 'var(--grn-accent-surface)', color: '#fff', borderRadius: '12px 12px 0 0' }}>
                        {['Sr. No.', 'Item', 'EAN', 'Net Qty', 'Net Revenue'].map((c, i) => (
                          <div key={c} style={{ padding: '11px 13px', fontSize: 10, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', textAlign: 'center' }}>{c}</div>
                        ))}
                      </div>
                      {(dash.top_skus || []).map((s, i) => (
                        <div key={i} style={{ display: 'grid', gridTemplateColumns: '0.5fr 2.2fr 1fr 1fr 1fr', background: i % 2 ? 'var(--grn-surface-subtle)' : 'var(--grn-surface)', borderBottom: '1px solid var(--grn-border)' }}>
                          <Cell>{i + 1}</Cell>
                          <Cell >{s.item_name}</Cell>
                          <Cell >{s.ean || <span style={{ color: 'var(--grn-border-soft)' }}>—</span>}</Cell>
                          <Cell bold>{fmtInt(s.net_qty_sold)}</Cell>
                          <Cell bold color="var(--grn-green)">₹{fmtInt(s.net_amount_sold)}</Cell>
                        </div>
                      ))}
                      {(!dash.top_skus || dash.top_skus.length === 0) && <div style={{ padding: 40, textAlign: 'center', color: 'var(--grn-text-muted)' }}>No sales data yet.</div>}
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {tab === 'projection' && (
          <>
            {error && (
              <div style={{ background: 'var(--grn-pink-tint)', color: 'var(--grn-error)', borderRadius: 10, padding: '12px 16px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertCircle size={16} /> {error}
              </div>
            )}

            {loading && !proj && <div style={{ ...card, padding: 46, textAlign: 'center', color: 'var(--grn-text-muted)' }}>Loading…</div>}

            {proj && (
              <>
                <KpiBento
                  items={(proj.zone_totals || []).map((z) => ({
                    label: `${z.zone} (${ZONE_CITY[z.zone]})`,
                    value: `${fmtInt(z.current_stock)} stock`,
                    sub: `${fmtInt(z.sku_count)} SKUs · ${fmtInt(z.gap > 0 ? z.gap : 0)} short`,
                  }))}
                />

                <div style={{ ...card, padding: '18px 20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--grn-text)' }}>Per-SKU 20-day projection ({filteredRows.length})</div>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                      <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search SKU..." style={inputStyle} />
                      <button onClick={loadProjection} style={ghostBtn}><RefreshCw size={13} /> Refresh</button>
                      <button onClick={handleExport} disabled={!proj} style={solidBtn}>Download Excel</button>
                    </div>
                  </div>
                  <ZoneFilterBar rows={allRows} value={zoneFilter} onChange={setZoneFilter} />
                  <StatusFilterBar rows={allRows.filter((r) => !zoneFilter || r.zone === zoneFilter)} value={statusFilter} onChange={setStatusFilter} />
                  <div style={{ overflow: 'auto' }}>
                    <div style={{ minWidth: 1000 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '0.9fr 2fr 1fr 1fr 1fr 1fr 0.9fr 0.9fr 1.2fr', background: 'var(--grn-accent-surface)', color: '#fff', borderRadius: '12px 12px 0 0' }}>
                        {['Zone', 'Item', 'EAN', 'Current Stock', 'In Transit', 'Avg Daily Sales', 'Needed 20d', 'Gap', 'Status'].map((c, i) => (
                          <div key={c} style={{ padding: '11px 13px', fontSize: 10, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', textAlign: 'center' }}>{c}</div>
                        ))}
                      </div>
                      {filteredRows.length === 0 && <div style={{ padding: 40, textAlign: 'center', color: 'var(--grn-text-muted)' }}>No rows.</div>}
                      {filteredRows.map((r, i) => (
                        <div key={i} style={{ display: 'grid', gridTemplateColumns: '0.9fr 2fr 1fr 1fr 1fr 1fr 0.9fr 0.9fr 1.2fr', background: i % 2 ? 'var(--grn-surface-subtle)' : 'var(--grn-surface)', borderBottom: '1px solid var(--grn-border)' }}>
                          <Cell >{r.zone} ({ZONE_CITY[r.zone]})</Cell>
                          <Cell >{r.item_name}</Cell>
                          <Cell >{r.ean || <span style={{ color: 'var(--grn-error)' }}>unmapped</span>}</Cell>
                          <Cell bold>{fmtInt(r.current_stock)}</Cell>
                          <Cell color={r.in_transit_qty > 0 ? '#1D4ED8' : undefined} title={r.in_transit_po ? `PO: ${r.in_transit_po}` : undefined}>{r.in_transit_qty > 0 ? `${fmtInt(r.in_transit_qty)}${r.in_transit_date ? ` (${r.in_transit_date})` : ''}` : '—'}</Cell>
                          <Cell>{r.avg_daily_sales}/day</Cell>
                          <Cell>{fmtInt(r.needed_20_days)}</Cell>
                          <Cell bold color={r.gap > 0 ? 'var(--grn-error)' : 'var(--grn-green)'}>{r.gap > 0 ? `+${r.gap}` : Math.abs(r.gap)}</Cell>
                          <div style={{ padding: '9px 13px', textAlign: 'center' }}>
                            <span style={{ fontSize: 10.5, fontWeight: 700, padding: '4px 9px', borderRadius: 8, background: STATUS[r.status]?.bg, color: STATUS[r.status]?.fg, border: `1px solid ${STATUS[r.status]?.border}` }}>
                              {STATUS[r.status]?.label || r.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {tab === 'stock' && (() => {
          const zoneQty = {}, zeroSkus = {};
          ZONES.forEach((z) => { zoneQty[z] = 0; zeroSkus[z] = 0; });
          stockRows.forEach((r) => {
            if (!ZONES.includes(r.zone)) return;
            zoneQty[r.zone] += Number(r.quantity) || 0;
            if (Number(r.quantity) === 0) zeroSkus[r.zone]++;
          });

          const pivoted = (() => {
            const map = new Map();
            filteredStock.forEach((r) => {
              const key = r.ean || r.item_name;
              if (!map.has(key)) map.set(key, { ean: r.ean, item_name: r.item_name, total: 0, North: 0, South: 0, East: 0, West: 0, in_transit_qty: r.in_transit_qty || 0, in_transit_date: r.in_transit_date || null, in_transit_po: r.in_transit_po || null });
              const row = map.get(key);
              row.total += Number(r.quantity) || 0;
              row[r.zone] = (row[r.zone] || 0) + (Number(r.quantity) || 0);
            });
            return [...map.values()];
          })();

          return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <KpiBento items={[
              ...ZONES.map((z) => ({ label: `${z} (${ZONE_CITY[z]}) Qty`, value: zoneQty[z] })),
              { label: 'Total Qty', value: totalStockQty },
            ]} columns="repeat(5,1fr)" />
            <KpiBento items={[
              ...ZONES.map((z) => ({ label: `${z} (${ZONE_CITY[z]}) Zero Stock SKUs`, value: zeroSkus[z] })),
            ]} columns="repeat(4,1fr)" />
            <KpiBento items={[
              { label: 'SOH Amount', value: totalStockValue, prefix: '₹', sub: '(MRP not available)' },
            ]} columns="repeat(auto-fit, minmax(220px, 1fr))" />

            <div style={{ ...card, padding: '18px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--grn-text)' }}>Stock Status ({stockZone ? filteredStock.length : pivoted.length})</div>
                <input value={stockSearch} onChange={(e) => setStockSearch(e.target.value)} placeholder="Search item or EAN..." style={inputStyle} />
              </div>
              <ZoneFilterBar rows={stockRows} value={stockZone} onChange={setStockZone} />
              <div style={{ overflowX: 'auto' }}>
                <table style={tableS}>
                  <thead>
                    <tr>
                      {(stockZone ? ['EAN', 'Item Name', 'Quantity', 'In Transit', 'Unit'] : ['EAN', 'Item Name', 'Total Qty', 'In Transit', ...ZONES.map((z) => `${z} (${ZONE_CITY[z]})`)]).map((h) => (
                        <th key={h} style={thS2}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {stockZone ? (
                      filteredStock.length === 0 ? (
                        <tr><td colSpan={5} style={{ padding: 30, textAlign: 'center', color: 'var(--grn-text-muted)' }}>No stock data</td></tr>
                      ) : filteredStock.map((r, i) => (
                        <tr key={i} style={{ background: i % 2 === 0 ? 'var(--grn-surface)' : 'var(--grn-surface-subtle)' }}>
                          <td style={{ ...td2, fontFamily: 'monospace', color: r.ean ? 'var(--grn-purple)' : 'var(--grn-error)' }}>{r.ean || 'Unmapped'}</td>
                          <td style={td2}>{r.item_name}</td>
                          <td style={{ ...td2, fontWeight: 700 }}>{fmtInt(r.quantity)}</td>
                          <td style={{ ...td2, color: r.in_transit_qty > 0 ? '#1D4ED8' : 'var(--grn-text-muted)' }} title={r.in_transit_po ? `PO: ${r.in_transit_po}` : undefined}>{r.in_transit_qty > 0 ? `${fmtInt(r.in_transit_qty)}${r.in_transit_date ? ` (${r.in_transit_date})` : ''}` : '—'}</td>
                          <td style={td2}>{r.unit}</td>
                        </tr>
                      ))
                    ) : (
                      pivoted.length === 0 ? (
                        <tr><td colSpan={8} style={{ padding: 30, textAlign: 'center', color: 'var(--grn-text-muted)' }}>No stock data</td></tr>
                      ) : pivoted.map((r, i) => (
                        <tr key={i} style={{ background: i % 2 === 0 ? 'var(--grn-surface)' : 'var(--grn-surface-subtle)' }}>
                          <td style={{ ...td2, fontFamily: 'monospace', color: r.ean ? 'var(--grn-purple)' : 'var(--grn-error)' }}>{r.ean || 'Unmapped'}</td>
                          <td style={td2}>{r.item_name}</td>
                          <td style={{ ...td2, fontWeight: 700 }}>{fmtInt(r.total)}</td>
                          <td style={{ ...td2, color: r.in_transit_qty > 0 ? '#1D4ED8' : 'var(--grn-text-muted)' }} title={r.in_transit_po ? `PO: ${r.in_transit_po}` : undefined}>{r.in_transit_qty > 0 ? `${fmtInt(r.in_transit_qty)}${r.in_transit_date ? ` (${r.in_transit_date})` : ''}` : '—'}</td>
                          <td style={td2}>{fmtInt(r.North)}</td>
                          <td style={td2}>{fmtInt(r.South)}</td>
                          <td style={td2}>{fmtInt(r.East)}</td>
                          <td style={td2}>{fmtInt(r.West)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          );
        })()}

        {tab === 'sales' && (() => {
          const filteredSales = salesRows
            .filter((r) => !salesZone || r.zone === salesZone)
            .filter((r) => !salesSearch || r.item_name?.toLowerCase().includes(salesSearch.toLowerCase()) || r.vch_bill_no?.toLowerCase().includes(salesSearch.toLowerCase()));
          return (
          <div style={{ ...card, padding: '18px 20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 4 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--grn-text)' }}>Sales ({filteredSales.length})</div>
                <div style={{ fontSize: 11, color: 'var(--grn-text-muted)', marginTop: 2 }}>One row per invoice line.</div>
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <input value={salesSearch} onChange={(e) => setSalesSearch(e.target.value)} placeholder="Search item or bill no..." style={inputStyle} />
                <button onClick={() => exportSheet({
                  headers: ['Zone', 'Date', 'Bill No.', 'Item', 'EAN', 'Customer', 'Qty', 'Amount', 'Type'],
                  rows: filteredSales.map((r) => [r.zone ? `${r.zone} (${ZONE_CITY[r.zone]})` : '', r.sale_date, r.vch_bill_no, r.item_name, r.ean || '', r.particulars, r.quantity, r.amount, r.is_credit_note ? 'Credit' : 'Sale']),
                  sheetName: 'Fatehpuria Sales',
                  filename: `fatehpuria-sales_${todayStamp()}.xlsx`,
                })} style={solidBtn}>Download Excel</button>
              </div>
            </div>
            {uploadMsgs.sales && <div style={{ fontSize: 11, color: uploadMsgs.sales.type === 'error' ? 'var(--grn-error)' : 'var(--grn-green)', marginBottom: 10 }}>{uploadMsgs.sales.text}</div>}
            <ZoneFilterBar rows={salesRows} value={salesZone} onChange={setSalesZone} />
            <div style={{ overflow: 'auto' }}>
              <div style={{ minWidth: 1000 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '0.7fr 0.9fr 0.8fr 1.6fr 1fr 1fr 0.7fr 0.9fr 0.9fr', background: 'var(--grn-accent-surface)', color: '#fff', borderRadius: '12px 12px 0 0' }}>
                  {['Zone', 'Date', 'Bill No.', 'Item', 'EAN', 'Customer', 'Qty', 'Amount', 'Type'].map((c, i) => (
                    <div key={c} style={{ padding: '11px 13px', fontSize: 10, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', textAlign: 'center' }}>{c}</div>
                  ))}
                </div>
                {filteredSales
                  .slice(0, 500)
                  .map((r, i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '0.7fr 0.9fr 0.8fr 1.6fr 1fr 1fr 0.7fr 0.9fr 0.9fr', background: r.is_credit_note ? 'var(--grn-pink-tint)' : i % 2 ? 'var(--grn-surface-subtle)' : 'var(--grn-surface)', borderBottom: '1px solid var(--grn-border)' }}>
                      <Cell >{r.zone ? `${r.zone} (${ZONE_CITY[r.zone]})` : '—'}</Cell>
                      <Cell >{formatDate(r.sale_date)}</Cell>
                      <Cell >{r.vch_bill_no}</Cell>
                      <Cell >{r.item_name}</Cell>
                      <Cell >{r.ean || <span style={{ color: 'var(--grn-error)' }}>unmapped</span>}</Cell>
                      <Cell >{r.particulars}</Cell>
                      <Cell>{fmtInt(r.quantity)}</Cell>
                      <Cell bold>₹{fmtInt(r.amount)}</Cell>
                      <Cell>
                        {r.is_credit_note
                          ? <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4, background: 'var(--grn-pink-tint)', color: 'var(--grn-error)' }}>Credit</span>
                          : <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4, background: 'var(--grn-green-tint)', color: 'var(--grn-green)' }}>Sale</span>}
                      </Cell>
                    </div>
                  ))}
                {salesRows.length === 0 && <div style={{ padding: 40, textAlign: 'center', color: 'var(--grn-text-muted)' }}>No sales data uploaded yet.</div>}
              </div>
            </div>
          </div>
          );
        })()}

        {tab === 'mapping' && (
          <DistributorSkuMasterTable
            nameCol="fatehpuria_name"
            nameLabel="Fatehpuria Name"
            canEdit={canEdit}
            endpoint="/fatehpuria-inventory"
            updatedBy={user?.username || 'Portal'}
          />
        )}
      </div>
    </div>
  );
}

function ZoneFilterBar({ rows, value, onChange }) {
  const counts = {};
  ZONES.forEach((z) => { counts[z] = 0; });
  rows.forEach((r) => { if (counts[r.zone] !== undefined) counts[r.zone]++; });
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
      <button onClick={() => onChange('')} style={{
        fontSize: 11.5, fontWeight: 700, padding: '6px 12px', borderRadius: 999, cursor: 'pointer',
        background: value === '' ? 'var(--grn-accent-surface)' : 'var(--grn-surface-subtle)', color: value === '' ? '#fff' : 'var(--grn-text-muted)', border: `1px solid ${value === '' ? 'var(--grn-accent-surface)' : 'var(--grn-border)'}`,
      }}>
        All zones ({rows.length})
      </button>
      {ZONES.map((zone) => {
        const active = value === zone;
        return (
          <button key={zone} onClick={() => onChange(active ? '' : zone)} style={{
            fontSize: 11.5, fontWeight: 700, padding: '6px 12px', borderRadius: 999, cursor: 'pointer',
            background: active ? 'var(--grn-purple)' : 'var(--grn-purple-tint)', color: active ? '#fff' : 'var(--grn-purple)', border: `1px solid ${active ? 'var(--grn-purple)' : 'var(--grn-border)'}`,
          }}>
            {zone} ({ZONE_CITY[zone]}) ({counts[zone]})
          </button>
        );
      })}
    </div>
  );
}

function StatusFilterBar({ rows, value, onChange }) {
  const counts = { OUT: 0, CRITICAL: 0, LOW: 0, HEALTHY: 0, NON_MOVING: 0 };
  rows.forEach((r) => { if (counts[r.status] !== undefined) counts[r.status]++; });
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
      <button onClick={() => onChange('')} style={{
        fontSize: 11.5, fontWeight: 700, padding: '6px 12px', borderRadius: 999, cursor: 'pointer',
        background: value === '' ? 'var(--grn-accent-surface)' : 'var(--grn-surface-subtle)', color: value === '' ? '#fff' : 'var(--grn-text-muted)', border: `1px solid ${value === '' ? 'var(--grn-accent-surface)' : 'var(--grn-border)'}`,
      }}>
        All ({rows.length})
      </button>
      {STATUS_ORDER.map((key) => {
        const s = STATUS[key];
        const active = value === key;
        return (
          <button key={key} onClick={() => onChange(active ? '' : key)} style={{
            fontSize: 11.5, fontWeight: 700, padding: '6px 12px', borderRadius: 999, cursor: 'pointer',
            background: active ? s.fg : s.bg, color: active ? '#fff' : s.fg, border: `1px solid ${s.border}`,
          }}>
            {s.label} ({counts[key]})
          </button>
        );
      })}
    </div>
  );
}

function Cell({ children, align = 'center', bold, color, title }) {
  return <div title={title} style={{ padding: '11px 13px', fontSize: 12.5, textAlign: align, fontWeight: bold ? 700 : 400, color: color || 'var(--grn-text)' }}>{children}</div>;
}

const inputStyle = { padding: '8px 11px', borderRadius: 10, border: '1px solid var(--grn-border)', background: 'var(--grn-surface-subtle)', fontFamily: FONT, fontSize: 12.5, minWidth: 200 };
const ghostBtn = { display: 'flex', alignItems: 'center', gap: 6, background: 'var(--grn-purple-tint)', color: 'var(--grn-purple)', border: 'none', borderRadius: 10, padding: '8px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const solidBtn = { background: 'var(--grn-green)', color: '#fff', border: 'none', borderRadius: 10, padding: '8px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const tableS = { width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: FONT };
const thS2 = { background: 'var(--grn-accent-surface)', color: '#fff', padding: '10px 14px', textAlign: 'center', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' };
const td2 = { padding: '11px 14px', borderBottom: '1px solid var(--grn-border)', color: 'var(--grn-text)', verticalAlign: 'middle', textAlign: 'center' };
